/**
 * AdminOnboardingPanel — shown on Admin's Employee Detail page.
 *
 * Surfaces the full onboarding + compliance state for one employee:
 *   - Overall status (Complete / Incomplete + how it was completed)
 *   - Terms & Policy versions the employee accepted vs the current
 *     published versions (spot outdated acks at a glance)
 *   - The 4 self-service checks (Profile / Documents / Policies / Terms)
 *   - Recent policy + procedure acknowledgements
 *   - Two admin actions:
 *       • Complete Onboarding    → override, requires an audit reason
 *       • Request Re-Ack         → clear a specific policy/procedure/terms
 *                                  ack so the employee is prompted again
 *   - Audit log of admin actions against this employee's onboarding
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { CheckCircle2, Circle, ShieldCheck, RotateCcw, History, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy, HH:mm'); }
  catch { return String(iso); }
};

const CheckRow = ({ label, complete, hint }) => (
  <div className="flex items-start gap-3 px-3 py-2 rounded-sm bg-muted/30">
    {complete ? (
      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
    ) : (
      <Circle className="w-4 h-4 text-muted-foreground/60 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
    )}
    <div className="min-w-0 flex-1">
      <p className={`text-xs font-medium uppercase tracking-wider ${complete ? 'text-emerald-500' : 'text-foreground'}`}>
        {label}
      </p>
      {hint ? <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p> : null}
    </div>
  </div>
);

const InfoRow = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-border/50 last:border-0">
    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
    <span className="text-sm text-foreground text-right truncate" data-testid={testId}>{value}</span>
  </div>
);

export default function AdminOnboardingPanel({ employeeId, onUpdated, readOnly = false }) {
  const [snapshot, setSnapshot] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [reackOpen, setReackOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reackKind, setReackKind] = useState('terms');
  const [reackTarget, setReackTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const [snap, aud, pol, proc] = await Promise.all([
        axios.get(`${API}/admin/employees/${employeeId}/onboarding/status`),
        axios.get(`${API}/admin/employees/${employeeId}/onboarding/audit`),
        axios.get(`${API}/policies`),
        axios.get(`${API}/procedures`),
      ]);
      setSnapshot({ ...snap.data, _policies: pol.data || [], _procedures: proc.data || [] });
      setAudit(aud.data || []);
    } catch (e) {
      console.warn('Onboarding panel fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleComplete = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error('Please provide a reason (min 3 chars)');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/employees/${employeeId}/onboarding/complete`,
        { reason: reason.trim() },
      );
      toast.success('Onboarding marked as complete');
      setCompleteOpen(false);
      setReason('');
      await fetchData();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete onboarding');
    } finally {
      setSaving(false);
    }
  };

  const handleReack = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error('Please provide a reason (min 3 chars)');
      return;
    }
    if (reackKind !== 'terms' && !reackTarget) {
      toast.error('Please pick a policy or procedure');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/employees/${employeeId}/onboarding/request-reack`,
        {
          kind: reackKind,
          id: reackKind === 'terms' ? null : reackTarget,
          reason: reason.trim(),
        },
      );
      toast.success(res.data?.message || 'Re-acknowledgement requested');
      setReackOpen(false);
      setReason('');
      setReackTarget('');
      await fetchData();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not request re-acknowledgement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border rounded-sm" data-testid="onboarding-panel-loading">
        <CardContent className="p-4 text-sm text-muted-foreground italic">Loading onboarding…</CardContent>
      </Card>
    );
  }
  if (!snapshot) {
    return null;
  }

  const s = snapshot;
  const c = s.checks || {};
  const isIncomplete = !!s.onboarding_required;
  const isOverride = s.onboarding_completed_method === 'admin_override';

  // Human hints per row (mirrors OnboardingBanner but read-only)
  const profileHint = c.profile?.missing?.length
    ? `Missing: ${c.profile.missing.slice(0, 5).join(', ')}${c.profile.missing.length > 5 ? '…' : ''}`
    : 'All required fields set';
  const docHint = c.documents?.missing?.length
    ? `Missing: ${c.documents.missing.slice(0, 3).join(', ')}${c.documents.missing.length > 3 ? '…' : ''}`
    : (c.documents?.expired?.length ? 'Some documents expired' : 'All mandatory documents uploaded');
  const polHint = (() => {
    const p = c.policies?.missing_policy_count || 0;
    const pr = c.policies?.missing_procedure_count || 0;
    if (p + pr === 0) return 'All acknowledged';
    return `${p} policy · ${pr} procedure pending`;
  })();
  const termsHint = c.terms?.complete
    ? `Accepted ${fmt(s.terms_accepted_at)}${s.terms_accepted_version ? ` (v${s.terms_accepted_version})` : ''}`
    : 'Not accepted';

  return (
    <>
      <Card className="bg-card border-border rounded-sm" data-testid="onboarding-panel">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Onboarding &amp; Compliance
              </p>
              <div className="flex items-center gap-2 mt-1">
                {isIncomplete ? (
                  <Badge className="bg-orange-500/15 text-orange-500 border-orange-500/40 text-[10px] uppercase tracking-wider" data-testid="onboarding-panel-status">
                    <AlertCircle className="w-3 h-3 mr-1" /> Incomplete
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/40 text-[10px] uppercase tracking-wider" data-testid="onboarding-panel-status">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                  </Badge>
                )}
                {isOverride ? (
                  <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/40 text-[10px] uppercase tracking-wider">
                    Admin Override
                  </Badge>
                ) : null}
              </div>
            </div>
            {!readOnly && (
            <div className="flex items-center gap-2">
              {isIncomplete ? (
                <Button
                  size="sm"
                  onClick={() => { setReason(''); setCompleteOpen(true); }}
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white uppercase tracking-wider text-xs h-8"
                  data-testid="admin-complete-onboarding-btn"
                >
                  <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                  Complete Onboarding
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setReason(''); setReackTarget(''); setReackKind('terms'); setReackOpen(true); }}
                className="rounded-sm border-border uppercase tracking-wider text-xs h-8"
                data-testid="request-reack-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                Request Re-Ack
              </Button>
            </div>
            )}
          </div>

          {/* Info rows */}
          <div className="border border-border rounded-sm" data-testid="onboarding-info-rows">
            <InfoRow label="Email Verified" value={s.email_verified ? 'Yes' : 'No'} testId="info-email-verified" />
            <InfoRow label="Email Verified On" value={fmt(s.email_verified_at)} testId="info-email-verified-at" />
            <InfoRow label="Onboarding Status" value={isIncomplete ? 'Incomplete' : 'Complete'} testId="info-status" />
            <InfoRow label="Onboarding Completed" value={fmt(s.onboarding_completed_at)} testId="info-completed-at" />
            <InfoRow label="Completed By" value={
              s.onboarding_completed_by_name
                ? `${s.onboarding_completed_by_name}${isOverride ? ' (admin override)' : ''}`
                : (isIncomplete ? '—' : 'Employee self-service')
            } testId="info-completed-by" />
            <InfoRow label="Terms Accepted On" value={fmt(s.terms_accepted_at)} testId="info-terms-accepted-at" />
            <InfoRow label="Terms Version Accepted" value={s.terms_accepted_version ? `v${s.terms_accepted_version}` : '—'} testId="info-terms-version" />
            <InfoRow label="Current Terms Version" value={s.current_terms_version ? `v${s.current_terms_version}` : '—'} testId="info-current-terms-version" />
            <InfoRow label="Current Policy Version (max)" value={s.current_policy_version ? `v${s.current_policy_version}` : '—'} testId="info-current-policy-version" />
            <InfoRow label="Current Procedure Version (max)" value={s.current_procedure_version ? `v${s.current_procedure_version}` : '—'} testId="info-current-procedure-version" />
          </div>

          {/* 4 self-service checks */}
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Self-service progress</p>
            <CheckRow label="Profile Completed" complete={!!c.profile?.complete} hint={profileHint} />
            <CheckRow label="Documents Uploaded" complete={!!c.documents?.complete} hint={docHint} />
            <CheckRow label="Company Policies Acknowledged" complete={!!c.policies?.complete} hint={polHint} />
            <CheckRow label="Terms & Conditions Accepted" complete={!!c.terms?.complete} hint={termsHint} />
          </div>

          {/* Audit log */}
          {audit.length > 0 ? (
            <div className="border-t border-border pt-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <History className="w-3 h-3" /> Admin action history
              </p>
              <div className="space-y-2" data-testid="onboarding-audit-list">
                {audit.map((row) => (
                  <div key={row.id} className="bg-muted/30 rounded-sm px-3 py-2" data-testid={`onboarding-audit-${row.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-foreground uppercase tracking-wider">
                        {row.action === 'admin_complete_onboarding' ? 'Admin marked as complete' : `Re-ack requested: ${row.extra?.label || row.extra?.kind || ''}`}
                      </p>
                      <p className="text-[10px] text-muted-foreground font-mono">{fmt(row.created_at)}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      by {row.admin_name || row.admin_email || row.admin_id}
                    </p>
                    {row.detail ? <p className="text-xs text-foreground/80 mt-1">&ldquo;{row.detail}&rdquo;</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Complete Onboarding dialog */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="admin-complete-onboarding-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider text-sm">Complete Onboarding (Admin Override)</DialogTitle>
            <DialogDescription>
              This will grant the employee full app access immediately, bypassing the four self-service checks. Recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-xs uppercase tracking-wider">Audit reason (required)</Label>
            <Textarea
              id="reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. In-person onboarding session completed at HQ on 2026-02-15"
              className="rounded-sm border-border"
              data-testid="complete-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)} className="rounded-sm" data-testid="complete-cancel-btn">Cancel</Button>
            <Button
              disabled={saving || reason.trim().length < 3}
              onClick={handleComplete}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="complete-confirm-btn"
            >
              {saving ? 'Recording…' : 'Complete Onboarding'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Re-Ack dialog */}
      <Dialog open={reackOpen} onOpenChange={setReackOpen}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="request-reack-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider text-sm">Request Re-Acknowledgement</DialogTitle>
            <DialogDescription>
              Clears the employee&apos;s acknowledgement for one document so they are prompted to sign the updated version again. Onboarding stays complete — only the selected document needs to be re-accepted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider">Document type</Label>
              <Select value={reackKind} onValueChange={(v) => { setReackKind(v); setReackTarget(''); }}>
                <SelectTrigger className="rounded-sm border-border" data-testid="reack-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="terms">Company Terms &amp; Conditions</SelectItem>
                  <SelectItem value="policy">Specific Policy</SelectItem>
                  <SelectItem value="procedure">Specific Procedure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reackKind === 'policy' && (
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider">Policy</Label>
                <Select value={reackTarget} onValueChange={setReackTarget}>
                  <SelectTrigger className="rounded-sm border-border" data-testid="reack-policy-select"><SelectValue placeholder="Choose a policy" /></SelectTrigger>
                  <SelectContent>
                    {(s._policies || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {reackKind === 'procedure' && (
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider">Procedure</Label>
                <Select value={reackTarget} onValueChange={setReackTarget}>
                  <SelectTrigger className="rounded-sm border-border" data-testid="reack-procedure-select"><SelectValue placeholder="Choose a procedure" /></SelectTrigger>
                  <SelectContent>
                    {(s._procedures || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="reack-reason" className="text-xs uppercase tracking-wider">Audit reason (required)</Label>
              <Input
                id="reack-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Policy XYZ updated to version 3"
                className="rounded-sm border-border"
                data-testid="reack-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReackOpen(false)} className="rounded-sm" data-testid="reack-cancel-btn">Cancel</Button>
            <Button
              disabled={saving || reason.trim().length < 3 || (reackKind !== 'terms' && !reackTarget)}
              onClick={handleReack}
              className="rounded-sm"
              data-testid="reack-confirm-btn"
            >
              {saving ? 'Requesting…' : 'Request Re-Ack'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
